import { COLLECTION_SCORING_WINDOW_MS } from '@vizit/api';
import type { CollectionImageView, ImageView } from '@vizit/api';

type ScoredImage = Pick<ImageView, 'image_type' | 'hero_score' | 'vizit_score'>;

/**
 * The image's unrounded score — `hero_score` for heroes, `vizit_score` otherwise.
 *
 * Only the "hide images not higher than the original" filter reads this. That filter is a
 * precision comparison, not a display: comparing the rounded `display_score` on both sides
 * would hide a 80.4 generated image behind a 79.6 original, since both round to 80. The
 * number on screen still comes from `display_score`.
 */
function rawComparisonScore(image: ScoredImage | undefined | null): number | null {
  if (!image) return null;

  const raw = image.image_type === 'hero' ? image.hero_score : image.vizit_score;
  if (raw === null || raw === undefined || raw === '') return null;

  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

type ProcessableImage = Pick<ImageView, 'image_type' | 'hero_score' | 'vizit_score' | 'url'>;

function hasServedUrl(image: Pick<ImageView, 'url'>): boolean {
  return image.url !== null && !!image.url?.includes('/images');
}

/** Whether the image has a raw score and served URL. */
function isProcessedImage(image: ProcessableImage): boolean {
  // `== null` covers both null and undefined.
  const rawScore = image.image_type === 'hero' ? image.hero_score : image.vizit_score;
  if (rawScore == null) return false;
  return hasServedUrl(image);
}

type PairListedImage = Pick<CollectionImageView, 'url' | 'pair_score' | 'created_at'>;

/**
 * Whether the pair it was listed under is still to score a collection image: its run is pending on it, or the image
 * landed after the pair was last requested, within the run's window. The API reopens the pair for a new image from
 * its landing, seconds later, so until then a missing pair score is not final. Any other image without a pair score
 * was never scored under the pair and won't be until the collection is scored again. `now` is when the image was
 * read, so a read past the window settles it.
 */
function isAwaitingPairScore(image: PairListedImage, pairRequestedAt: string, now = Date.now()): boolean {
  const pairScore = image.pair_score;
  if (pairScore?.scored) return false;
  if (pairScore?.scoring_status === 'pending') return true;
  const createdAtMs = image.created_at ? Date.parse(image.created_at) : Number.NaN;
  return createdAtMs > Date.parse(pairRequestedAt) && now - createdAtMs <= COLLECTION_SCORING_WINDOW_MS;
}

/** Whether a collection image has a served URL and the pair it was listed under is done with it, scored or not. */
function isProcessedUnderPair(image: PairListedImage, pairRequestedAt: string, now = Date.now()): boolean {
  return hasServedUrl(image) && !isAwaitingPairScore(image, pairRequestedAt, now);
}

export { hasServedUrl, isAwaitingPairScore, isProcessedImage, isProcessedUnderPair, rawComparisonScore };
export type { ProcessableImage };
